import { API_BASE_URL } from '../constants';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  buildUrl(endpoint) {
    const base = (this.baseURL || '').replace(/\/+$/, '');
    let path = (endpoint || '').startsWith('/') ? endpoint : `/${endpoint || ''}`;

    // If base is '/api' and endpoint already starts with '/api/', avoid '/api/api/...'
    // if (base === '/api' && path.startsWith('/api/')) {
    //   path = path.slice('/api'.length);
    // }

    return `${base}${path}`;
  }

  getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  async request(endpoint, options = {}) {
    const url = this.buildUrl(endpoint);
    const config = {
      headers: this.getAuthHeaders(),
      ...options
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        let errorDetail;
        try {
          const errorData = await response.json();
          errorDetail = errorData?.detail;
        } catch (e) {
          errorDetail = undefined;
        }
        throw new Error(errorDetail || `HTTP error! status: ${response.status}`);
      }

      // 204 No Content or empty body: return null
      if (response.status === 204) {
        return null;
      }

      const text = await response.text();
      if (!text) {
        return null;
      }

      return JSON.parse(text);
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Auth endpoints
  async login(credentials) {
    const body = new URLSearchParams();
    // FastAPI's OAuth2PasswordRequestForm expects 'username' and 'password'
    body.append('username', credentials.email || credentials.username);
    body.append('password', credentials.password);

    return this.request('/api/v1/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body,
    });
  }

  async register(userData) {
    return this.request('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async getCurrentUser() {
    return this.request('/api/v1/auth/me');
  }

  // User management
  async getUsers() {
    return this.request('/api/v1/users');
  }

  // Jobs endpoints
  async getJobs() {
    return this.request('/api/v1/jobs');
  }

  async createJob(jobData) {
    return this.request('/api/v1/jobs', {
      method: 'POST',
      body: JSON.stringify(jobData)
    });
  }

  async updateJob(jobId, jobData) {
    return this.request(`/api/v1/jobs/${jobId}`, {
      method: 'PUT',
      body: JSON.stringify(jobData)
    });
  }

  async updateJobStatus(jobId, status) {
    return this.request(`/api/v1/jobs/${jobId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }

  async deleteJob(jobId) {
    return this.request(`/api/v1/jobs/${jobId}`, {
      method: 'DELETE'
    });
  }

  // Interview endpoints
  async getAllInterviews() {
    // Backend automatically filters interviews by user role
    // No need to pass job IDs - simpler and more efficient
    return this.request('/api/v1/interviews');
  }

  async getJobInterviews(jobId) {
    return this.request(`/api/v1/jobs/${jobId}/interviews`);
  }

  async createInterview(jobId, interviewData) {
    return this.request(`/api/v1/jobs/${jobId}/interviews`, {
      method: 'POST',
      body: JSON.stringify(interviewData)
    });
  }

  async updateInterview(interviewId, interviewData) {
    return this.request(`/api/v1/interviews/${interviewId}`, {
      method: 'PUT',
      body: JSON.stringify(interviewData)
    });
  }

  async updateInterviewStatus(interviewId, status) {
    return this.request(`/api/v1/interviews/${interviewId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }

  async addInterviewFeedback(interviewId, feedbackData) {
    return this.request(`/api/v1/interviews/${interviewId}/feedback`, {
      method: 'PATCH',
      body: JSON.stringify(feedbackData)
    });
  }

  async deleteInterview(interviewId) {
    return this.request(`/api/v1/interviews/${interviewId}`, {
      method: 'DELETE'
    });
  }

  async getUpcomingInterviews() {
    return this.request('/api/v1/interviews/upcoming');
  }

  // Dashboard endpoints
  async getDashboardStats() {
    return this.request('/api/v1/dashboard/stats');
  }

  // Insights endpoints
  async getInsightsSummary() {
    return this.request('/api/v1/insights/summary');
  }

  async getBDPerformance(period = null, startDate = null, endDate = null) {
    let url = '/api/v1/insights/bd-performance';
    const params = new URLSearchParams();
    
    if (startDate && endDate) {
      params.append('start_date', startDate);
      params.append('end_date', endDate);
    } else if (period) {
      params.append('period', period);
    }
    
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
    
    return this.request(url);
  }

  async getBDJobs(bdName, period = '24h') {
    return this.request(`/api/v1/insights/bd-jobs/${encodeURIComponent(bdName)}?period=${period}`);
  }

  async getJobTrend(period = 'week') {
    return this.request(`/api/v1/insights/trend?period=${period}`);
  }

  // BD-specific endpoints for their own performance
  async getMyBDPerformance(period = null, startDate = null, endDate = null) {
    let url = '/api/v1/insights/my-performance';
    const params = new URLSearchParams();
    
    if (startDate && endDate) {
      params.append('start_date', startDate);
      params.append('end_date', endDate);
    } else if (period) {
      params.append('period', period);
    }
    
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
    
    return this.request(url);
  }

  async getMyBDJobs(period = '24h') {
    return this.request(`/api/v1/insights/my-jobs?period=${period}`);
  }
}

const apiService = new ApiService();
export default apiService;